import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocalStorage } from '@vastra/ui';
import type { Order } from '@vastra/types';
import type { OrderListOutcome } from '@vastra/constants';
import { api } from '../services/api';
import { resolveCustomerApiToken } from '../lib/cognitoCustomerAuth';
import { useAuth } from './AuthContext';

interface OrdersContextValue {
  orders: Order[];
  setOrders: React.Dispatch<React.SetStateAction<Order[]>>;
  addOrder: (order: Order) => void;
  /** Merge or replace by id from server when JWT is present. */
  mergeOrder: (order: Order) => void;
  getOrderById: (id: string) => Order | undefined;
  /** Server-side list filter when logged in (default successful). */
  ordersListOutcome: OrderListOutcome;
  setOrdersListOutcome: React.Dispatch<React.SetStateAction<OrderListOutcome>>;
  /** Re-fetch the order list from the API (e.g. when opening My orders). */
  refreshOrders: () => Promise<void>;
}

const OrdersContext = createContext<OrdersContextValue | null>(null);

export function useOrders() {
  const ctx = useContext(OrdersContext);
  if (!ctx) throw new Error('useOrders must be used within OrdersProvider');
  return ctx;
}

const ORDERS_KEY = 'vastra_orders';

function sortOrdersDesc(list: Order[]): Order[] {
  return [...list].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

interface OrdersProviderProps {
  children: React.ReactNode;
}

export function OrdersProvider({ children }: OrdersProviderProps) {
  const { token, isAuthenticated } = useAuth();
  const [orders, setOrders] = useLocalStorage<Order[]>(ORDERS_KEY, []);
  const [ordersListOutcome, setOrdersListOutcome] = useState<OrderListOutcome>('successful');

  const addOrder = useCallback(
    (order: Order) => {
      setOrders((prev) => {
        const rest = prev.filter((o) => o.id !== order.id);
        return sortOrdersDesc([order, ...rest]);
      });
    },
    [setOrders]
  );

  const mergeOrder = useCallback(
    (order: Order) => {
      setOrders((prev) => {
        const rest = prev.filter((o) => o.id !== order.id);
        return sortOrdersDesc([order, ...rest]);
      });
    },
    [setOrders]
  );

  const getOrderById = useCallback(
    (id: string) => orders.find((o) => o.id === id),
    [orders]
  );

  const refreshOrders = useCallback(async () => {
    if (!isAuthenticated || !api.customerApiBase()) return;
    const authToken = await resolveCustomerApiToken(token);
    if (!authToken) return;
    try {
      const remote = await api.fetchCustomerOrders(authToken, ordersListOutcome);
      setOrders(sortOrdersDesc(remote));
    } catch {
      /* offline or session not valid for API */
    }
  }, [isAuthenticated, token, ordersListOutcome, setOrders]);

  useEffect(() => {
    if (!isAuthenticated || !api.customerApiBase()) return;
    let cancelled = false;
    void (async () => {
      const authToken = await resolveCustomerApiToken(token);
      if (!authToken || cancelled) return;
      try {
        const remote = await api.fetchCustomerOrders(authToken, ordersListOutcome);
        if (cancelled) return;
        setOrders(sortOrdersDesc(remote));
      } catch {
        /* offline or session not valid for API */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, isAuthenticated, setOrders, ordersListOutcome]);

  const value = useMemo(
    () => ({
      orders,
      setOrders,
      addOrder,
      mergeOrder,
      getOrderById,
      ordersListOutcome,
      setOrdersListOutcome,
      refreshOrders,
    }),
    [orders, setOrders, addOrder, mergeOrder, getOrderById, ordersListOutcome, refreshOrders]
  );

  return <OrdersContext.Provider value={value}>{children}</OrdersContext.Provider>;
}
